// Meter (HP / XP / progress), Stat tiles and the animated Counter.
import { useEffect, useRef, useState, type HTMLAttributes, type ReactNode } from 'react';
import { motionReduced } from './Motion';

export type MeterKind = 'gold' | 'hp' | 'xp' | 'plum';

export function Meter({ value, max, kind = 'gold', label, showValue = true, format, size, indeterminate, className = '', ...rest }: {
  value: number; max: number; kind?: MeterKind; label?: ReactNode; showValue?: boolean; format?: (value: number, max: number) => ReactNode; size?: 'sm' | 'lg'; indeterminate?: boolean; className?: string; 'data-testid'?: string;
}) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  const low = kind === 'hp' && pct <= 50 && pct > 25, critical = kind === 'hp' && pct <= 25;
  const cls = ['fk-meter', `fk-meter--${kind}`, low ? 'fk-meter--low' : '', critical ? 'fk-meter--critical' : '', size ? `fk-meter--${size}` : '', indeterminate ? 'fk-meter--indeterminate' : '', className].filter(Boolean).join(' ');
  return (
    <div className={cls} role="progressbar" aria-valuemin={0} aria-valuemax={max} aria-valuenow={indeterminate ? undefined : value} aria-label={typeof label === 'string' ? label : undefined} {...rest}>
      {(label || showValue) && <div className="fk-meter__head">{label && <span className="fk-meter__label">{label}</span>}{showValue && !indeterminate && <span className="fk-meter__value">{format ? format(value, max) : `${value} / ${max}`}</span>}</div>}
      <div className="fk-meter__track"><div className="fk-meter__fill" style={{ ['--fk-meter-pct' as string]: `${pct}%` }} /></div>
    </div>
  );
}

export function Stat({ label, value, unit, delta, className = '', ...rest }: { label: ReactNode; value: ReactNode; unit?: ReactNode; delta?: 'up' | 'down'; className?: string; 'data-testid'?: string }) {
  return <div className={`fk-stat ${delta ? `fk-stat--delta-${delta}` : ''} ${className}`} {...rest}><span className="fk-stat__label">{label}</span><span className="fk-stat__value">{value}{unit && <small>{unit}</small>}</span></div>;
}

export function Stats({ children, className = '', ...rest }: { children: ReactNode; className?: string } & HTMLAttributes<HTMLDivElement>) { return <div className={`fk-stats ${className}`} {...rest}>{children}</div>; }

const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

/** Tabular number that rolls from its previous value to the new one (600 ms, ease-out). */
export function Counter({ value, duration = 600, format = (v: number) => String(Math.round(v)), className = '', ...rest }: { value: number; duration?: number; format?: (v: number) => string; className?: string; 'data-testid'?: string }) {
  const [shown, setShown] = useState(value);
  const from = useRef(value);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const start = from.current;
    if (start === value) return;
    if (motionReduced()) { from.current = value; setShown(value); return; }
    let raf = 0; let t0: number | undefined;
    const step = (t: number) => {
      t0 ??= t;
      const p = Math.min(1, (t - t0) / duration);
      setShown(start + (value - start) * easeOut(p));
      if (p < 1) raf = requestAnimationFrame(step); else { from.current = value; setTick((n) => n + 1); }
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <span key={tick} className={`fk-counter ${tick ? 'fk-counter--tick' : ''} ${className}`} {...rest}>{format(shown)}</span>;
}
